// One provider's calendar connection for one person, as shown on a Team
// directory card: its state; Connect (or Reconnect) when this viewer may
// connect it; and Disconnect only when the parent passes onDisconnect, which
// it does on the viewer's OWN card alone (the server refuses anyone else's).
//
// A read-only badge (someone else's card, for a viewer who cannot manage it)
// never calls the per-person status route, which refuses that viewer; it shows
// the state from the workspace-wide list instead (knownConnected).
import React from "react";

const PROVIDERS = {
  google: {
    statusPath: "/api/google/connected",
    startPath: "/api/google/oauth/start",
    windowName: "google-oauth",
  },
  microsoft: {
    statusPath: "/api/ms/connected",
    startPath: "/api/ms/oauth/start",
    windowName: "ms-oauth",
  },
};

export default function CalendarConnectBadge({
  provider = "google",
  email,
  onChange,
  icon,
  label = "Google",
  disabled = false,
  readOnly = false,
  knownConnected,
  onDisconnect,
  disconnecting = false,
  refreshKey = 0,
  apiBase = "https://api.bossdrop.ai",
}) {
  const cfg = PROVIDERS[provider] || PROVIDERS.google;
  const base = String(apiBase || "").replace(/\/+$/, "");
  // "connected" | "disconnected" | "reconnect" | null. The status routes read
  // stored rows only (no token refresh), so polling them is cheap.
  const [status, setStatus] = React.useState(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const refresh = React.useCallback(async () => {
    if (!email) return false;
    let s = null;
    try {
      const r = await fetch(`${base}${cfg.statusPath}?email=${encodeURIComponent(email)}`, {
        credentials: "include",
      });
      // A refused or failed check is "not connected", never a crash.
      s = r.ok ? await r.json().catch(() => null) : null;
    } catch {
      s = null;
    }
    const ok = !!s?.connected;
    setStatus(ok ? "connected" : s?.needsReconnect ? "reconnect" : "disconnected");
    onChangeRef.current?.(ok);
    return ok;
  }, [base, cfg.statusPath, email]);

  React.useEffect(() => {
    if (!readOnly) refresh();
  }, [readOnly, refresh, refreshKey]);

  function startConnect() {
    if (disabled || readOnly) return;
    const w = window.open(
      `${base}${cfg.startPath}?email=${encodeURIComponent(email)}`,
      cfg.windowName,
      "width=520,height=650",
    );
    const timer = setInterval(async () => {
      const ok = await refresh();
      if (ok) {
        clearInterval(timer);
        try {
          w?.close();
        } catch {}
      }
    }, 1200);
    setTimeout(() => clearInterval(timer), 180000);
  }

  if (!email) return null;

  const iconImg = icon ? (
    <img src={icon} alt="" aria-hidden="true" style={{ height: 14, width: "auto" }} />
  ) : null;

  const shown = readOnly
    ? knownConnected === true
      ? "connected"
      : knownConnected === false
        ? "disconnected"
        : "unknown"
    : status;

  if (shown === "connected") {
    return (
      <>
        <span
          className="tag"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
          title={`${label} connected`}
        >
          {iconImg}
          {label}: Connected
        </span>
        {typeof onDisconnect === "function" ? (
          <button
            type="button"
            className="btn ghost"
            onClick={onDisconnect}
            disabled={disconnecting}
            aria-busy={disconnecting}
            aria-label={`Disconnect ${label}`}
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </button>
        ) : null}
      </>
    );
  }

  if (readOnly) {
    return (
      <span
        className="tag"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.75 }}
        title={shown === "unknown" ? `${label} status isn't available` : `${label} not connected`}
      >
        {iconImg}
        {label}: {shown === "unknown" ? "Not known" : "Not connected"}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="btn"
      onClick={startConnect}
      disabled={disabled}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      title={status === "reconnect" ? `Reconnect ${label}` : `Connect ${label}`}
    >
      {iconImg}
      {status === "reconnect" ? `Reconnect ${label}` : `Connect ${label}`}
    </button>
  );
}
